export interface ApiError {
  status?: number;
  data?:
    | string
    | {
        error?: {
          code?: string;
          message?: string;
        };
        message?: string;
      };
  message?: string;
  response?: {
    data?: string;
  };
}

/**
 * Extracts a user-friendly error message from various API error formats
 * @param error - The error object from API responses
 * @param defaultMessage - Default message to use if no specific error message is found
 * @returns A formatted error message string
 */
export const getApiErrorMessage = (error: unknown, defaultMessage: string = "An error occurred"): string => {
  if (!error) {
    return defaultMessage;
  }

  const apiError = error as ApiError;

  if (typeof apiError.data === "string") {
    return apiError.data;
  }

  if (apiError.data?.error?.message) {
    return apiError.data.error.message;
  }

  if (apiError.data?.message) {
    return apiError.data.message;
  }

  if (apiError.response?.data) {
    return apiError.response.data;
  }

  if (apiError.message) {
    return apiError.message;
  }

  if (typeof error === "object" && error !== null) {
    const serializedError = error as { error?: string; message?: string };
    if (serializedError.error) {
      return serializedError.error;
    }
    if (serializedError.message) {
      return serializedError.message;
    }
  }

  return defaultMessage;
};

/**
 * Handles API errors and displays appropriate toast messages
 * @param error - The error object from API responses
 * @param defaultMessage - Default message to use if no specific error message is found
 * @param toastFunction - Optional custom toast function, defaults to console.error
 */
export const handleApiError = (
  error: unknown,
  defaultMessage: string = "An error occurred",
  toastFunction?: (message: string) => void,
): void => {
  const errorMessage = getApiErrorMessage(error, defaultMessage);

  if (toastFunction) {
    toastFunction(errorMessage);
  } else {
    console.error("API Error:", errorMessage);
  }
};
